import Zone from "@insite/client-framework/Components/Zone";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import Block from "@store/Components/Common/Block";
import StoreInvoiceDetailPageTypeLink from "@store/Components/Store/StoreInvoiceDetailPageTypeLink";
import StoreOrderBillingInformation from "@store/Components/Store/StoreOrderBillingInformation";
import StoreOrderShippingInformation from "@store/Components/Store/StoreOrderShippingInformation";
import { checkoutBlockProps, checkoutInfoGridProps, checkoutInfoProps } from "@store/theme/common/props/checkout";
import getWidgetComponent from "@store/utilities/getWidgetComponent";
import React, { FC, useContext } from "react";

const Date = getWidgetComponent("OrderDetails/OrderDate");
const Notes = getWidgetComponent("OrderDetails/OrderDetailsNotes");
const Status = getWidgetComponent("OrderDetails/OrderDetailsStatus");

interface Props extends WidgetProps {}

export interface OrderDetailsInformationStyles {
    infoGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    infoLabel?: TypographyPresentationProps;
    infoValue?: TypographyPresentationProps;
    notesGridItem?: GridItemProps;
}

export const orderDetailsInformationStyles: OrderDetailsInformationStyles = {
    infoGridContainer: checkoutInfoGridProps.container,
    infoGridItem: checkoutInfoGridProps.item,
    infoLabel: checkoutInfoProps.label,
    infoValue: checkoutInfoProps.value,
    notesGridItem: {
        ...checkoutInfoGridProps.largeItem,
        width: [12, 8, 12, 8, 8],
    },
};

const styles = orderDetailsInformationStyles;

const OrderDetailsInformation: FC<Props> = () => {
    const { value: order } = useContext(OrderStateContext);

    if (!order) {
        return null;
    }

    return (
        <>
            <Block {...checkoutBlockProps}>
                {(order.customerPO || order.notes || order.statusDisplay || order.orderDate || order.terms) && (
                    <GridContainer {...styles.infoGridContainer}>
                        {order.orderDate && (
                            <GridItem {...styles.infoGridItem}>
                                <Date />
                            </GridItem>
                        )}
                        {order.statusDisplay && (
                            <GridItem {...styles.infoGridItem}>
                                <Status />
                            </GridItem>
                        )}
                        {order.properties?.invoiceNumberToDisplay && (
                            <GridItem {...styles.infoGridItem}>
                                <Typography {...styles.infoLabel}>{translate("Invoice Number")}</Typography>
                                <Typography {...styles.infoValue}>
                                    <StoreInvoiceDetailPageTypeLink
                                        title={order.properties.invoiceNumberToDisplay}
                                        invoiceNumber={order.properties.invoiceNumber}
                                        isLink={!!order.properties.orderNumberToDisplay}
                                    />
                                </Typography>
                            </GridItem>
                        )}
                        {order.terms && (
                            <GridItem {...styles.infoGridItem}>
                                <Typography {...styles.infoLabel}>{translate("Terms")}</Typography>
                                <Typography {...styles.infoValue}>{translate(`${order.terms}`)}</Typography>
                            </GridItem>
                        )}
                        {order.customerPO && (
                            <GridItem {...styles.infoGridItem}>
                                <Typography {...styles.infoLabel}>{translate("PO Number")}</Typography>
                                <Typography {...styles.infoValue}>{order.customerPO}</Typography>
                            </GridItem>
                        )}
                        {order.properties?.jobNumber && (
                            <GridItem {...styles.infoGridItem}>
                                <Typography {...styles.infoLabel}>{translate("Job Number")}</Typography>
                                <Typography {...styles.infoValue}>{order.properties?.jobNumber}</Typography>
                            </GridItem>
                        )}
                        {order.notes && (
                            <GridItem {...styles.notesGridItem}>
                                <Notes />
                            </GridItem>
                        )}
                    </GridContainer>
                )}
            </Block>
            <Block {...checkoutBlockProps}>
                <StoreOrderBillingInformation />
            </Block>
            <Block {...checkoutBlockProps}>
                <StoreOrderShippingInformation />
            </Block>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: OrderDetailsInformation,
    definition: {
        group: "Order Details",
        displayName: "Order Information",
        allowedContexts: ["OrderDetailsPage"],
    },
};

export default widgetModule;
